import React, { FC } from 'react';
import { useTheme } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { GridRowId, useGridApiContext } from '@mui/x-data-grid-pro';

import { ITimeCategoryBaseDTO } from 'src/api/dtos';
import CommonTimesheetRepository from 'src/api/repositories/common/v1/timesheet.repository';
import { ChevronDownIcon } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';

const NONE_ABSENCE_AND_OVERRIDE = 'none';

interface Props {
  id: GridRowId;
  field: string;
  absenceAndOverride: ITimeCategoryBaseDTO | null;
  onAbsenceAndOverrideChange: (absenceAndOverride: ITimeCategoryBaseDTO | null) => void;
}

export const TimesheetAbsenceOverrideEditCell: FC<Props> = ({
  id,
  field,
  absenceAndOverride,
  onAbsenceAndOverrideChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslate();
  const apiRef = useGridApiContext();

  const {
    data: timeCategories,
    isLoading,
    isFetching,
  } = CommonTimesheetRepository.endpoints.useGetTimeCategoriesQuery(
    { isMemo: false },
    {
      selectFromResult: ({ data, ...results }) => ({ data: data ?? [], ...results }),
    },
  );

  const loading = isLoading || isFetching;

  const handleChange = (event: SelectChangeEvent<number>) => {
    const timeCategoryId = event.target.value;

    if (timeCategoryId === NONE_ABSENCE_AND_OVERRIDE) {
      onAbsenceAndOverrideChange(null);
      apiRef.current.stopCellEditMode({ id, field });

      return;
    }

    const selectedTimeCategory = timeCategories.find((timeCategory) => timeCategory.id === Number(timeCategoryId));

    if (!selectedTimeCategory) {
      return;
    }

    onAbsenceAndOverrideChange(selectedTimeCategory);
    apiRef.current.stopCellEditMode({ id, field });
  };

  return (
    <Select
      value={absenceAndOverride?.id ?? ''}
      onChange={handleChange}
      variant="standard"
      defaultOpen
      autoFocus
      displayEmpty
      disableUnderline
      IconComponent={(props) => <ChevronDownIcon color={theme.palette.text.secondary} className={props.className} />}
      sx={{ width: '100%', '.MuiSelect-select:focus': { background: 'transparent' } }}
    >
      {loading && (
        <MenuItem>
          <Typography fontSize="0.875rem">{t('global.loading')}</Typography>
        </MenuItem>
      )}
      {!loading && (
        <MenuItem value={NONE_ABSENCE_AND_OVERRIDE}>
          <Typography fontSize="0.875rem">{t('global.none')}</Typography>
        </MenuItem>
      )}
      {timeCategories.map((timeCategory, index) => (
        <MenuItem key={index} value={timeCategory.id}>
          <Typography fontSize="0.875rem">{timeCategory.description}</Typography>
        </MenuItem>
      ))}
    </Select>
  );
};
